import { createContext, useCallback, useContext, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

// The operator app's sign-in: password + emailed code at /api/admin/login|verify, or a passkey at
// /api/admin/passkeys/login. Every section (CRM, service, planner, sourcing) uses this one token.
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "vortex_admin_token";
// Customer-facing links (QR feedback cards) always point at the public site, even when the app is
// opened at crm.vortexvs.com.
export const PUBLIC_SITE = "https://www.vortexvs.com";

// "Keep me signed in" puts the token in localStorage (survives closing the browser, 30 days);
// otherwise sessionStorage (gone when the tab closes, 12 hours at most).
export const readToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};

export const storeToken = (t, remember) => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    if (t) (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, t);
  } catch { /* storage blocked */ }
};

export const errText = (err, fallback = "Something went wrong. Please try again.") => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const SessionContext = createContext(null);

export const useSessionState = () => {
  const [token, setTokenState] = useState(readToken);

  const setToken = useCallback((t, { remember = false } = {}) => {
    storeToken(t, remember);
    setTokenState(t || "");
  }, []);
  // A 401 only clears the token here; choosing "Sign out" also ends the session on the server.
  const clearToken = useCallback(() => setToken(""), [setToken]);
  const signOut = useCallback(() => {
    const t = readToken();
    if (t) axios.post(`${API}/admin/logout`, null, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    setToken("");
  }, [setToken]);

  // Shared error handler: a 401 ends the session (the layout then shows sign-in), anything else is a toast.
  const onAuthError = useCallback((err, fallback) => {
    if (err?.response?.status === 401) {
      clearToken();
      toast.error("Session expired. Sign in again.");
    } else {
      toast.error(errText(err, fallback));
    }
  }, [clearToken]);

  const api = useMemo(() => {
    const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
    // A 401 ends the session; callers report the error themselves (usually through onAuthError).
    client.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 401) clearToken();
      return Promise.reject(err);
    });
    return client;
  }, [token, clearToken]);

  return { token, setToken, signOut, onAuthError, api };
};

export const SessionProvider = SessionContext.Provider;

export const useSession = () => useContext(SessionContext);
